import { useMemo } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import InputField from 'dashboard/components/FormControls/InputField';
import PhoneInputField from 'dashboard/components/FormControls/PhoneInputField';
import TimezoneSelectField from 'dashboard/components/FormControls/TimezoneSelectField';
import Form from 'dashboard/layouts/FormLayout/Form';
import Section from 'dashboard/layouts/FormLayout/Section';
import { MfaMethod, User } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import { isRequired, Rule, withMessage } from 'lib/utils/formValidation';
import isValidPhone from 'lib/utils/formValidation/isValidPhone';

const validate: Record<keyof FormCurrentUserPersonalDetailsData, Rule[]> = {
  'name-first': [withMessage('Please enter your first name', isRequired)],
  'name-last': [withMessage('Please enter your last name', isRequired)],
  'phone-number': [withMessage('The phone number you entered is not valid', isValidPhone)],
  timezone: [],
};

interface FormCurrentUserPersonalDetailsProps {
  currentUser: User;
  samlEnabled?: boolean;
}

const FormCurrentUserPersonalDetails = ({
  currentUser,
  handleSubmit,
  submitting,
  pristine,
  samlEnabled = false,
}: InjectedFormProps & FormCurrentUserPersonalDetailsProps) => {
  // This is phrased as 'cannot remove' because phone number is required to
  // enable phone MFA in the first place
  const validatePhoneNumber = useMemo(
    () =>
      (currentUser.attributes['enabled-mfa-methods'] ?? []).includes(MfaMethod.Phone)
        ? validate['phone-number'].concat(
            withMessage(
              'Cannot remove phone number because phone 2-factor authentication is enabled',
              isRequired,
            ),
          )
        : validate['phone-number'],
    [currentUser],
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Section cols={2} colsMobile={1}>
        <Field
          component={InputField}
          name='name-first'
          id='name-first'
          type='text'
          data-test='form-current-user-personal-details__first-name'
          disabled={submitting || samlEnabled}
          validate={validate['name-first']}
          placeholder='First'
          label='First name'
        />
        <Field
          component={InputField}
          name='name-last'
          id='name-last'
          type='text'
          data-test='form-current-user-personal-details__last-name'
          disabled={submitting || samlEnabled}
          validate={validate['name-last']}
          placeholder='Last'
          label='Last name'
        />
      </Section>
      <Section cols={2} colsMobile={1}>
        <Field
          component={PhoneInputField}
          name='phone-number'
          id='phone-number'
          type='text'
          data-test='form-current-user-personal-details__phone-number'
          disabled={submitting || samlEnabled}
          placeholder='Phone number'
          validate={validatePhoneNumber}
          label='Phone number'
        />

        <Field
          component={TimezoneSelectField}
          name='timezone'
          id='timezone'
          placeholder='Timezone'
          data-test='form-current-user-personal-details__timezone'
          disabled={submitting}
          label='Timezone'
        />
      </Section>

      <Section cols={3} colsMobile={1}>
        <Button
          type='submit'
          data-test='form-current-user-personal-details__save'
          disabled={submitting || pristine}
          isPending={submitting}
        >
          Save changes
        </Button>
      </Section>
    </Form>
  );
};

export type FormCurrentUserPersonalDetailsData = Pick<
  User['attributes'],
  'name-first' | 'name-last' | 'phone-number' | 'timezone'
>;

export const FORM_CURRENT_USER_PERSONAL_DETAILS_NAME = 'formCurrentUserPersonalDetails';

export default reduxForm<FormCurrentUserPersonalDetailsData, FormCurrentUserPersonalDetailsProps>({
  form: FORM_CURRENT_USER_PERSONAL_DETAILS_NAME,
  enableReinitialize: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormCurrentUserPersonalDetails);
